import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Grid, Typography, Dialog, DialogContent, Button, Alert } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import LoadingOverlay from "../../../containers/LoadingOverlay/LoadingOverlay";
import { EmployeeRef } from "../../../models/employees";
import { ExpenseReportDTO } from "../../../models/expense-reports";
import { ExpenseReportsApprovalFilter } from "../../../models/expense-reports/expense-reports-approval-filter";
import {
  clientAcceptExpenseReportsByLink,
  clientRejectExpenseReportsByLink,
} from "../../../services/my-client-team-pending-approvals/my-client-team-expense-reports-api.service";
import { EXPENSE_REPORT_STATUS } from "../../../utils/constants/constants";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { OVERRIDEN_ERROR_MESSAGES } from "../../../utils/toastify-response/overriden-error-messages";
import HeaderPortal from "../../my-t-and-a/HeaderPortal";
import ExpenseReportContent from "../../travel-and-expenses/ExpenseReport/AddEditExpenseReport/ExpenseReportContent/ExpenseReportContent";
import { ER_ALERT_BY_LINK_ID } from "../../travel-and-expenses/ExpenseReport/AddEditExpenseReport/ExpenseReportContent/utils";
import { ExpenseReportView } from "../../travel-and-expenses/ExpenseReport/utils";
import { SidebarLike } from "../SidebarLike/SidebarLike";
import {
  HeaderAndContentContainer,
  MainContainer,
  Container,
  ContentContainer,
  Content,
  HeaderContainer,
  TitleContainer,
  Title,
  StatusAmountContainer,
  StatusAmountBlock,
} from "../styles";

interface State {
  expenseReport: ExpenseReportDTO | null;
  error: string;
  loading: boolean;
  isDialogOpen: boolean;
}
const ClientExpenseReportsApprovalsByLink = ({ approveReject }: { approveReject: "approve" | "reject" }) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const expenseReportId = searchParams.get("er");
  const userId = searchParams.get("u");

  const [state, setState] = useState<State>({
    expenseReport: null,
    error: "",
    loading: false,
    isDialogOpen: true,
  });
  const loadExpenseReport = useCallback(async () => {
    if (expenseReportId) {
      setState((state) => ({ ...state, loading: true }));
      try {
        const response =
          approveReject === "approve"
            ? await clientAcceptExpenseReportsByLink({
                expenseReportIDs: [expenseReportId],
                userId,
              } as ExpenseReportsApprovalFilter)
            : await clientRejectExpenseReportsByLink({
                expenseReportIDs: [expenseReportId],
                userId,
              } as ExpenseReportsApprovalFilter);

        if (!response.error) {
          setState((state) => ({ ...state, expenseReport: response.items[0] }));
        } else {
          setState((state) => ({ ...state, error: response.error, expenseReport: response.items[0] }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    }
  }, []);

  useEffect(() => {
    loadExpenseReport();
  }, []);

  return (
    <>
      <LoadingOverlay loading={state.loading}>
        <MainContainer>
          <Container>
            <SidebarLike />
            <HeaderAndContentContainer>
              <HeaderContainer>
                <TitleContainer>
                  <Title>{`${approveReject === "approve" ? "Approve" : "Reject"} expense report`}</Title>
                  <Divider orientation="vertical" />
                  <Typography>{state.expenseReport?.employee?.name}</Typography>
                </TitleContainer>

                <HeaderPortal
                  title={`${approveReject === "approve" ? "Approve" : "Reject"} expense report`}
                  employee={<Typography>{state.expenseReport?.employee?.name}</Typography>}
                />
              </HeaderContainer>

              <ContentContainer>
                <Content sx={{ height: "100%" }}>
                  {state.error && (
                    <Alert
                      id={ER_ALERT_BY_LINK_ID}
                      style={{ marginBottom: "10px" }}
                      severity={`${OVERRIDEN_ERROR_MESSAGES.includes(state.error) ? "info" : "error"}`}
                    >
                      {`${
                        OVERRIDEN_ERROR_MESSAGES.includes(state.error)
                          ? `${state.error} by ${state.expenseReport?.acceptedRejectedByUser?.name ?? ""} on ${
                              state.expenseReport?.acceptedRejectedAt
                                ? `${intlFormatDateShort(new Date(state.expenseReport?.acceptedRejectedAt))}`
                                : ""
                            }`
                          : `${state.error}`
                      }`}
                    </Alert>
                  )}

                  {state.expenseReport && (
                    <>
                      <StatusAmountContainer>
                        <StatusAmountBlock>
                          <Typography> Status : </Typography>
                          <Typography> {state.expenseReport?.status?.name} </Typography>
                          {state.expenseReport?.documentation &&
                          (state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.submitted.id ||
                            state.expenseReport?.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id) ? (
                            <Typography variant="body2">(Documentation audit marked as OK)</Typography>
                          ) : (
                            ""
                          )}
                        </StatusAmountBlock>
                        <StatusAmountBlock>
                          <Typography sx={{ fontWeight: "bold" }}>Total :</Typography>
                          <Typography>{state.expenseReport?.totalAmount?.toLocaleCustom()}</Typography>
                          <Typography>{state.expenseReport?.currency?.code ?? ""}</Typography>
                        </StatusAmountBlock>
                      </StatusAmountContainer>
                      <ExpenseReportContent
                        employee={state.expenseReport?.employee as EmployeeRef}
                        expenseReport={state.expenseReport}
                        view={ExpenseReportView.employee}
                        wholeReportIsReadOnly
                        hideAuditNotes
                        isManagedByLink
                      />
                    </>
                  )}
                  <Dialog open={state.isDialogOpen}>
                    <DialogContent>
                      <Grid
                        style={{
                          padding: "10px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "30px",
                        }}
                      >
                        {state.loading ? (
                          <LoadingOverlay loading={state.loading}>
                            <Typography variant="h5">
                              {approveReject === "approve" ? "Approving expense report" : "Rejecting expense report"}
                            </Typography>
                          </LoadingOverlay>
                        ) : state.expenseReport && !state.error ? (
                          approveReject === "approve" ? (
                            <Typography variant="h5">
                              {" "}
                              Expense report approved
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                color={theme.palette.success.main}
                              />
                            </Typography>
                          ) : (
                            <Typography variant="h5">
                              {" "}
                              Expense report rejected
                              <FontAwesomeIcon
                                icon={faCircleCheck}
                                color={theme.palette.success.main}
                              />
                            </Typography>
                          )
                        ) : state.error ? (
                          <Alert severity={`${OVERRIDEN_ERROR_MESSAGES.includes(state.error) ? "info" : "error"}`}>
                            {`${
                              OVERRIDEN_ERROR_MESSAGES.includes(state.error)
                                ? `${state.error} ${state.expenseReport?.acceptedRejectedByUser?.name ?? ""} on ${
                                    state.expenseReport?.acceptedRejectedAt
                                      ? `${intlFormatDateShort(new Date(state.expenseReport?.acceptedRejectedAt))}`
                                      : ""
                                  }`
                                : `${state.error}`
                            }`}
                          </Alert>
                        ) : (
                          <Typography variant="h5">
                            {approveReject === "approve" ? "Already approved" : "Already rejected"}
                          </Typography>
                        )}
                        <Button
                          variant={"contained"}
                          onClick={() => setState((state) => ({ ...state, isDialogOpen: false }))}
                          style={{ width: "fit-content", alignSelf: "center" }}
                          disabled={state.loading}
                        >
                          Okay
                        </Button>
                      </Grid>
                    </DialogContent>
                  </Dialog>
                </Content>
              </ContentContainer>
            </HeaderAndContentContainer>
          </Container>
        </MainContainer>
      </LoadingOverlay>
    </>
  );
};
export default ClientExpenseReportsApprovalsByLink;
